import * as React from "react"
import styled from "styled-components"
import { REPORT_SIZE, REPORT_SIZE_SCALING, reportDataPlans, reportTerminals } from "../reports-helpers"
import { ReportData, ReportOptions } from ".."
import _ from "lodash"
import { chunks } from "../../utils/helpers"
import ReportPageTemplate from "../pages/ReportPageTemplate"
import ReportDetails from "./ReportDetails"
import ReportTerminalsPage, { TERMINALS_PER_PAGE } from "../pages/ReportTerminalsPage"
import ReportDataPlanPage from "../pages/ReportDataPlanPage"
import { ChartSettings } from "../../components/organisms/charts/SharedDataChartView"

export type ReportProps = {
  options: ReportOptions
  data: ReportData
}

export const A4 = styled.div`
  width: ${REPORT_SIZE[0] * REPORT_SIZE_SCALING}px;
  height: ${REPORT_SIZE[1] * REPORT_SIZE_SCALING}px;
  background-color: ${(props) => props.theme.colors.white};
`

const reportName = (data: ReportData) => {
  switch (data.type) {
    case "dataplan":
      return {
        title: `Dataplan #${data.dataPlan.apiPlanId}`,
        subtitle: data.sharedPlan ? "Shared dataplan report" : "Single dataplan report",
      }
    case "group":
      return {
        title: data.group.name,
        subtitle: "Group report",
      }
    case "terminal":
      return {
        title: "Terminal report",
      }
  }
}

const ReportPreamblePage = ({ data }: { data: ReportData }) => {
  return (
    <ReportPageTemplate {...reportName(data)}>
      <ReportDetails
        details={[
          {
            name: "Report Created Date",
            type: "text",
            value: new Date(Date.now()).toLocaleString(),
          },
          {
            name: "No. Terminals",
            value: reportTerminals(data).length.toString(),
            type: "text",
          },
          {
            name: "No. Dataplans",
            value: reportDataPlans(data).length.toString(),
            type: "text",
          },
        ]}
      />
    </ReportPageTemplate>
  )
}

const ReportTerminals = ({ data }: { data: ReportData }) => {
  const allTerminals = reportTerminals(data)
  const terminalLists = chunks(allTerminals, TERMINALS_PER_PAGE)

  return (
    <>
      {terminalLists.map((terminals, i) => (
        <ReportTerminalsPage
          key={terminals.map(({ terminal }) => terminal.id).join(",")}
          data={data}
          index={i}
          total={allTerminals.length}
          terminals={terminals}
        />
      ))}
    </>
  )
}

const ReportDataPlans = ({ data, chartSettings }: { data: ReportData; chartSettings: ChartSettings }) => {
  return (
    <>
      {reportDataPlans(data)?.map((dataPlan) => (
        <ReportDataPlanPage key={dataPlan?.apiPlanId} dataPlan={dataPlan!} chartSettings={chartSettings} />
      ))}
    </>
  )
}

const Report = React.forwardRef<HTMLDivElement, ReportProps>(({ data, options }, forwardedRef) => (
  <A4 ref={forwardedRef}>
    <ReportPreamblePage data={data} />
    <ReportDataPlans data={data} chartSettings={options} />
    <ReportTerminals data={data} />
  </A4>
))

export default Report
